"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { type BatchLine, planBatch } from "@/lib/tool-kit/identifiers/batch";
import {
  renderBibDrafts,
  webpageDraft,
} from "@/lib/tool-kit/identifiers/bibtex-entries";
import {
  inputLines,
  normalizeDoi,
  normalizeWebUrl,
} from "@/lib/tool-kit/identifiers/identifier-input";
import {
  type CiteEntry,
  citeLookup,
  type LookupFailure,
} from "@/lib/tool-kit/identifiers/lookup";
import { useLookupRun } from "@/lib/tool-kit/identifiers/use-lookup-run";

/** English strings. A translated content file overrides these through `ui`. */
export const URL_TO_BIBTEX_STRINGS = {
  inputLabel: "Web addresses — one per line",
  inputPlaceholder:
    "https://www.who.int/news-room/fact-sheets/detail/malaria\nwww.nasa.gov/missions/artemis",
  inputHint:
    "Full links or bare addresses (https:// is added). Up to {max} per run.",
  formatLabel: "Output",
  formatBibtex: "BibTeX — @misc with howpublished = \\url{…}",
  formatBiblatex: "biblatex — @online with url and urldate",
  accessedLabel: "Accessed on",
  accessedHint:
    "The date you read the pages. It goes in note (BibTeX) or urldate (biblatex).",
  protectCapitals: "Brace capitalised title words so BibTeX keeps their case",
  asciiSafe: "ASCII-safe output — accents as LaTeX commands, e.g. {\\'e}",
  convert: "Convert to BibTeX",
  converting: "Reading {done} of {total}…",
  emptyPrompt: "Paste at least one web address, then convert.",
  results: "BibTeX entries",
  entryCount: "one:{count} entry|other:{count} entries",
  copy: "Copy BibTeX",
  copied: "Copied!",
  download: "Download .bib",
  checkEntries:
    "Pages often leave the author or date out of their metadata. Check each entry against the page before you cite it.",
  statusHeading: "Lookup results",
  found: "Read",
  failed: "Not converted",
  hasDoi:
    "{title} — this page has a DOI ({doi}); DOI to BibTeX gives a fuller @article entry.",
  notAUrl: "Not a web address. Start it with https:// or www.",
  duplicate: "Same address as an earlier line — converted once.",
  overLimit: "Over the {max}-address limit — run it in a second batch.",
  failBlocked:
    "The site refused our request — many news and journal sites block automated readers even when the page opens in your browser. Type this entry by hand.",
  failNotFound: "The site has no page at that address. Check the link.",
  failRateLimited:
    "The site is rate-limiting requests. Wait a minute and try again.",
  failUnavailable: "The site is not responding properly. Try again shortly.",
  failTimeout: "The site took too long to respond. Try again.",
  failNetwork:
    "The page could not be read — check the address and your connection.",
  noneConverted:
    "No address could be converted — the reasons are listed below.",
  unexpected: "Something went wrong while reading the pages. Please try again.",
} as const;

type Strings = typeof URL_TO_BIBTEX_STRINGS;
type Format = "bibtex" | "biblatex";

const MAX_URLS = 25;
const BIB_MIME = "application/x-bibtex;charset=utf-8";

interface Row extends BatchLine<string | null> {
  entry?: CiteEntry;
  reason?: LookupFailure;
}

/** A web address, or a DOI written as its doi.org link. */
function toUrl(line: string): string | null {
  const url = normalizeWebUrl(line);
  if (url) {
    return url;
  }
  const doi = normalizeDoi(line);
  return doi ? `https://doi.org/${doi}` : null;
}

function planRows(text: string): Row[] {
  return planBatch(inputLines(text), toUrl, (url) => url, MAX_URLS);
}

/** Today in the reader's own time zone, as YYYY-MM-DD. */
function localIsoDate(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function failureText(reason: LookupFailure | undefined, strings: Strings) {
  const messages: Record<LookupFailure, string> = {
    blocked: strings.failBlocked,
    "not-found": strings.failNotFound,
    "rate-limited": strings.failRateLimited,
    unavailable: strings.failUnavailable,
    timeout: strings.failTimeout,
    network: strings.failNetwork,
  };
  return reason ? messages[reason] : strings.failNetwork;
}

function rowDetail(row: Row, strings: Strings): string {
  switch (row.state) {
    case "invalid":
      return strings.notAUrl;
    case "duplicate":
      return strings.duplicate;
    case "over":
      return t(strings.overLimit, { max: MAX_URLS });
    case "found":
      return row.entry?.DOI
        ? t(strings.hasDoi, {
            title: row.entry.title ?? "",
            doi: row.entry.DOI,
          })
        : (row.entry?.title ?? "");
    default:
      return failureText(row.reason, strings);
  }
}

function StatusList({ rows, strings }: { rows: Row[]; strings: Strings }) {
  return (
    <ul className="space-y-2 text-sm">
      {rows.map((row) => {
        const ok = row.state === "found";
        return (
          <li
            className="rounded-lg border border-border bg-card p-3"
            key={row.id}
          >
            <span
              className={
                ok
                  ? "font-medium text-emerald-700 dark:text-emerald-400"
                  : "font-medium text-destructive"
              }
            >
              {ok ? strings.found : strings.failed}
            </span>{" "}
            <span className="break-all font-mono text-foreground">
              {row.parsed ?? row.input}
            </span>
            <p className="mt-1 text-muted-foreground">
              {rowDetail(row, strings)}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

export default function UrlToBibtexWidget() {
  const strings = useToolUi(URL_TO_BIBTEX_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [format, setFormat] = useState<Format>("bibtex");
  const [accessed, setAccessed] = useState("");
  const [protect, setProtect] = useState(false);
  const [ascii, setAscii] = useState(false);
  const { rows, progress, failed, run } = useLookupRun<Row>();

  // The date is the reader's "today", so it is set after mount — never during render.
  useEffect(() => {
    setAccessed((current) => current || localIsoDate());
  }, []);
  function handleConvert() {
    run(
      planRows(text),
      (row, signal) => citeLookup(row.parsed ?? "", signal),
      (row, result): Row =>
        result.ok
          ? { ...row, state: "found", entry: result.value }
          : { ...row, state: "failed", reason: result.reason }
    );
  }

  const output = useMemo(() => {
    const options = {
      protectCapitals: protect,
      asciiSafe: ascii,
      format,
      accessed,
    };
    return renderBibDrafts(
      rows.flatMap((row) =>
        row.entry && row.parsed
          ? [webpageDraft(row.entry, row.parsed, options)]
          : []
      )
    );
  }, [rows, protect, ascii, format, accessed]);
  const found = rows.filter((row) => row.state === "found").length;
  const running = progress !== null;
  const finished = rows.length > 0 && !running;

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={t(strings.inputHint, { max: MAX_URLS })}
        id="u2b-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={5}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.formatLabel}
          name="u2b-format"
          onChange={setFormat}
          options={[
            { value: "bibtex", label: strings.formatBibtex },
            { value: "biblatex", label: strings.formatBiblatex },
          ]}
          value={format}
        />
        <TextField
          hint={strings.accessedHint}
          id="u2b-accessed"
          label={strings.accessedLabel}
          onChange={setAccessed}
          type="date"
          value={accessed}
        />
      </div>
      <div className="space-y-2">
        <Checkbox
          checked={protect}
          label={strings.protectCapitals}
          onChange={setProtect}
        />
        <Checkbox
          checked={ascii}
          label={strings.asciiSafe}
          onChange={setAscii}
        />
      </div>
      <button
        className={BUTTON_PRIMARY}
        disabled={running || inputLines(text).length === 0}
        onClick={handleConvert}
        type="button"
      >
        {progress
          ? t(strings.converting, {
              done: fmt(progress.done),
              total: fmt(progress.total),
            })
          : strings.convert}
      </button>

      <div aria-live="polite" className="space-y-6">
        {failed ? <ErrorNotice>{strings.unexpected}</ErrorNotice> : null}
        {rows.length === 0 ? <Notice>{strings.emptyPrompt}</Notice> : null}
        {finished && found > 0 ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className={WIDGET_H2}>{strings.results}</h2>
                <p className="text-muted-foreground text-sm">
                  {tPlural(strings.entryCount, locale, found, {
                    count: fmt(found),
                  })}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copy}
                  text={output}
                />
                <button
                  className={BUTTON_SECONDARY}
                  onClick={() =>
                    downloadText("references.bib", output, BIB_MIME)
                  }
                  type="button"
                >
                  {strings.download}
                </button>
              </div>
            </div>
            <pre className="max-h-96 overflow-auto rounded-lg border border-border bg-card p-4 font-mono text-foreground text-xs leading-relaxed">
              {output}
            </pre>
            <p className="text-muted-foreground text-xs">
              {strings.checkEntries}
            </p>
          </div>
        ) : null}
        {finished && found === 0 ? (
          <ErrorNotice>{strings.noneConverted}</ErrorNotice>
        ) : null}
        {finished ? (
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.statusHeading}</h3>
            <StatusList rows={rows} strings={strings} />
          </div>
        ) : null}
      </div>
    </section>
  );
}
